import { FormEvent, useEffect, useState } from 'react';
import type { ProductListItem, RegisterRef, SaleReturnResult } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney, kop, lineAmount } from '../format';

interface Line {
  productId: string;
  name: string;
  price: number;
  qty: number;
}

// Çeksiz qaytarma (KT-101): müştərinin çeki yoxdur — mal cari pərakəndə qiymətlə qaytarılır, nağd verilir, səbəb məcburi.
// Kassir üçün menecer PİN-i. Eyni pəncərədə təkrar göndəriş eyni UUID ilə gedir.
export function NoReceiptReturn({ onDone }: { onDone: (r: SaleReturnResult) => void }) {
  const [registers, setRegisters] = useState<RegisterRef[]>([]);
  const [registerId, setRegisterId] = useState('');
  const [q, setQ] = useState('');
  const [found, setFound] = useState<ProductListItem[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [needPin, setNeedPin] = useState(false);
  const [id, setId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .shiftRegisters()
      .then((r) => {
        setRegisters(r);
        if (r.length > 0) setRegisterId(r[0].id);
      })
      .catch(() => setRegisters([]));
  }, []);

  async function search(e: FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    try {
      setFound((await api.products({ q: q.trim(), limit: 8 })).items.filter((p) => p.retailPrice !== null));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Axtarış alınmadı');
    }
  }

  function add(p: ProductListItem) {
    setLines((cur) => (cur.some((l) => l.productId === p.id) ? cur : [...cur, { productId: p.id, name: p.name, price: p.retailPrice!, qty: 1 }]));
    setFound([]);
    setQ('');
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.saleReturnNoReceipt({ id, registerId, lines: lines.map((l) => ({ productId: l.productId, qty: l.qty })), reason: reason.trim(), pin: pin || null });
      setLines([]);
      setReason('');
      setNeedPin(false);
      setId(crypto.randomUUID());
      onDone(r);
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'noreceipt_pin' || err.code === 'pin_invalid')) setNeedPin(true);
      setError(err instanceof ApiError ? err.message : 'Qaytarma alınmadı');
    } finally {
      setPin('');
      setBusy(false);
    }
  }

  const total = lines.reduce((s, l) => s + kop(lineAmount(l.qty, l.price)), 0) / 100;

  return (
    <section className="card" aria-label="Çeksiz qaytarma">
      <h2>Çeksiz qaytarma</h2>
      <p className="muted small">Çek yoxdursa: mal cari pərakəndə qiymətlə qaytarılır, pul nağd verilir. Səbəb yazılmalıdır.</p>
      {registers.length > 1 && (
        <label className="field">
          Kassa
          <select value={registerId} onChange={(e) => setRegisterId(e.target.value)}>
            {registers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code}
              </option>
            ))}
          </select>
        </label>
      )}
      <form onSubmit={search} role="search" className="toolbar">
        <input className="search" aria-label="Qaytarılan mal" placeholder="Malın adı və ya barkodu" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" className="btn-small">
          Malı tap
        </button>
      </form>
      {found.length > 0 && (
        <ul className="product-list">
          {found.map((p) => (
            <li key={p.id}>
              <button type="button" className="product-row" onClick={() => add(p)}>
                <span className="product-name">{p.name}</span>
                <span className="mono">{formatMoney(p.retailPrice)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {lines.map((l, i) => (
        <div className="purchase-line" key={l.productId}>
          <span className="product-name">{l.name}</span>
          <label>
            Miqdar
            <input type="number" min="0" step="0.001" value={l.qty} onChange={(e) => setLines((cur) => cur.map((x, k) => (k === i ? { ...x, qty: Number(e.target.value) } : x)))} />
          </label>
          <span className="cart-sum">{formatMoney(lineAmount(l.qty, l.price))}</span>
          <button type="button" className="link-btn" onClick={() => setLines((cur) => cur.filter((_, k) => k !== i))}>
            Sil
          </button>
        </div>
      ))}
      {lines.length > 0 && (
        <>
          <p>
            Qaytarılacaq nağd: <strong>{formatMoney(total)}</strong>
          </p>
          <label className="field">
            <span>
              Səbəb <span className="req" aria-hidden="true">*</span>
            </span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
          </label>
          {needPin && (
            <label className="field">
              Menecer PİN-i
              <input type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} />
            </label>
          )}
          <button type="button" className="btn-primary" onClick={() => void submit()} disabled={busy || !registerId || reason.trim().length < 3 || lines.some((l) => !(l.qty > 0))}>
            Çeksiz qaytarmanı təsdiqlə
          </button>
        </>
      )}
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
